package com.example.crashbit

import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Star
import androidx.compose.ui.input.pointer.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.withTransform
import androidx.compose.ui.input.pointer.*
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource
import crashbit_multiplatform.composeapp.generated.resources.*
import kotlin.math.*
import kotlin.random.Random

@Composable
fun SpaceSandbox(viewModel: SpaceViewModel) {
    var dragStart by remember { mutableStateOf<Offset?>(null) }
    var dragCurrent by remember { mutableStateOf<Offset?>(null) }

    val transformState = rememberTransformableState { zoomChange, panChange, _ ->
        if (viewModel.inputMode == InputMode.OFF) {
            viewModel.scale = (viewModel.scale * zoomChange).coerceIn(0.005f, 10f)
            if (!viewModel.isFollowing) viewModel.pan += panChange / viewModel.scale
        }
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize().background(Color.Black).transformable(state = transformState)) {
        val isLandscape = maxWidth > maxHeight

        SimulationLayer(
            bodiesProvider = { viewModel.bodies },
            starsProvider = { viewModel.stars },
            nebulaeProvider = { viewModel.nebulae },
            wormholesProvider = { viewModel.wormholes },
            effectsProvider = { viewModel.activeEffects },
            scaleProvider = { viewModel.scale },
            panProvider = { viewModel.pan },
            selectedBodyIdProvider = { viewModel.selectedBodyId },
            isFollowingProvider = { viewModel.isFollowing },
            isSurfaceViewActiveProvider = { viewModel.isSurfaceViewActive },
            surfacePitchAngleProvider = { viewModel.surfacePitchAngle },
            surfaceYawAngleProvider = { viewModel.surfaceYawAngle },
            statsProvider = { viewModel.stats },
            gameTimeProvider = { viewModel.gameTimeNanos },
            drawVersionProvider = { viewModel.drawVersion },
            trajectoriesProvider = { viewModel.trajectories },
            isHabitableZoneVisibleProvider = { viewModel.isHabitableZoneVisible },
            isPausedProvider = { viewModel.isPaused },
            inputModeProvider = { viewModel.inputMode },
            dragStartProvider = { dragStart },
            dragCurrentProvider = { dragCurrent },
            pendingWormholeIdProvider = { viewModel.pendingWormholeId },
            editingWormholeIdProvider = { viewModel.editingWormholeId },
            screenCenterProvider = { viewModel.screenCenter },
            screenSizeProvider = { viewModel.screenSize },
            interpolationAlphaProvider = { viewModel.interpolationAlpha },
            performanceMeterVisibleProvider = { viewModel.isPerformanceMeterVisible },
            onFpsUpdated = { viewModel.fps = it },
            modifier = Modifier
                .fillMaxSize()
                .onSizeChanged { viewModel.updateScreenSize(it) }
                .pointerInput(Unit) {
                    awaitPointerEventScope {
                        var previousTouchDistance = -1f
                        while (true) {
                            val event = awaitPointerEvent(PointerEventPass.Initial)
                            val pressedChanges = event.changes.filter { it.pressed }
                            
                            if (pressedChanges.size >= 2) {
                                val c1 = pressedChanges[0]
                                val c2 = pressedChanges[1]
                                val p1 = c1.position
                                val p2 = c2.position
                                val currentDistance = sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y))
                                
                                if (previousTouchDistance > 0f && currentDistance > 0f) {
                                    val zoomFactor = currentDistance / previousTouchDistance
                                    if (zoomFactor in 0.5f..2.0f) {
                                        viewModel.scale = (viewModel.scale * zoomFactor).coerceIn(0.005f, 10f)
                                        c1.consume()
                                        c2.consume()
                                    }
                                }
                                previousTouchDistance = currentDistance
                            } else {
                                previousTouchDistance = -1f
                            }

                            if (event.type == PointerEventType.Scroll) {
                                val scrollDelta = event.changes.firstOrNull()?.scrollDelta?.y ?: 0f
                                if (scrollDelta > 0) viewModel.zoomOut() else if (scrollDelta < 0) viewModel.zoomIn()
                            }
                        }
                    }
                }
                .pointerInput(viewModel.isCustomPlacing, viewModel.inputMode, viewModel.isFollowing, viewModel.isSurfaceViewActive) { 
                    if (viewModel.isSurfaceViewActive) {
                        detectDragGestures { change, dragAmount ->
                            change.consume()
                            viewModel.updateSurfacePitchAndYaw(dragAmount.x, dragAmount.y)
                        }
                    } else if (!viewModel.isFollowing) {
                        if (viewModel.isCustomPlacing) detectTapGestures { offset ->
                            val worldPos = (offset - viewModel.screenCenter) / viewModel.scale - viewModel.pan
                            when (viewModel.selectedPlacementType) {
                                PlacementType.STAR -> viewModel.addCustomStar(worldPos)
                                PlacementType.NEBULA -> viewModel.addCustomNebula(worldPos)
                                PlacementType.WORMHOLE -> if (isSaveSupported) viewModel.addCustomWormhole(worldPos)
                            }
                        } else when (viewModel.inputMode) {
                            InputMode.OFF -> awaitPointerEventScope {
                                while (true) {
                                    val event = awaitPointerEvent(); val down = event.changes.firstOrNull() ?: continue
                                    val selectedId = viewModel.selectedBodyId; val isFollowing = viewModel.isFollowing
                                    val followedBody = if (isFollowing && selectedId != null) (viewModel.stars.find { it.id == selectedId } ?: viewModel.bodies.find { it.id == selectedId }) else null
                                    val currentPan = if (followedBody != null) -followedBody.position else viewModel.pan
                                    val downWorldPos = (down.position - viewModel.screenCenter) / viewModel.scale - currentPan
                                    val grabbedW = viewModel.wormholes.find { (it.position - downWorldPos).getDistance() * viewModel.scale < it.radius * viewModel.scale }
                                    if (grabbedW != null) {
                                        viewModel.editingWormholeId = grabbedW.id
                                        var drag = awaitTouchSlopOrCancellation(down.id) { change, over -> if (over.getDistance() > 0) change.consume() }
                                        if (drag != null) while (true) {
                                            val mEvent = awaitPointerEvent(); val move = mEvent.changes.firstOrNull { it.id == down.id } ?: break
                                            if (!move.pressed) break; val moveWorldPos = (move.position - viewModel.screenCenter) / viewModel.scale - currentPan
                                            val vector = moveWorldPos - grabbedW.position; viewModel.setWormholeAngle(grabbedW.id, (atan2(vector.y.toDouble(), vector.x.toDouble()) * 180.0 / PI).toFloat()); move.consume()
                                        } else viewModel.rotateWormhole(grabbedW.id)
                                        viewModel.editingWormholeId = null
                                    } else if (down.changedToUp()) {
                                        val worldPos = downWorldPos; var sFI = false; val allB = (viewModel.bodies + viewModel.stars)
                                        for (body in allB) {
                                            val relPos = (body.position + currentPan) * viewModel.scale + viewModel.screenCenter
                                            if (relPos.x < 0 || relPos.x > viewModel.screenSize.width || relPos.y < 0 || relPos.y > viewModel.screenSize.height) {
                                                val angle = atan2(body.position.y + currentPan.y, body.position.x + currentPan.x)
                                                val indX = (cos(angle) * (viewModel.screenSize.width / 2f - 80f)) + viewModel.screenSize.width / 2f
                                                val indY = (sin(angle) * (viewModel.screenSize.height / 2f - 80f)) + viewModel.screenSize.height / 2f
                                                if ((down.position - Offset(indX, indY)).getDistance() < 80f) { viewModel.selectBody(body.id); sFI = true; break }
                                            }
                                        }
                                        if (!sFI) {
                                            val nearest = allB.minByOrNull { (it.position - worldPos).getDistance() }
                                            if (nearest != null && (nearest.position - worldPos).getDistance() * viewModel.scale < 250f) viewModel.selectBody(nearest.id) else if (!isFollowing) viewModel.deselectBody()
                                        }
                                    }
                                }
                            }
                            InputMode.YEET -> detectDragGestures(onDragStart = { dragStart = (it - viewModel.screenCenter) / viewModel.scale - viewModel.pan; dragCurrent = dragStart }, onDrag = { change, _ -> dragCurrent = (change.position - viewModel.screenCenter) / viewModel.scale - viewModel.pan }, onDragEnd = { if (dragStart != null && dragCurrent != null) viewModel.yeetPlanet(dragStart!!, (dragStart!! - dragCurrent!!) * 4f, viewModel.selectedPlanetSize.mass, viewModel.selectedPlanetSize.radius, Color.hsv(Random.nextFloat() * 360f, 1.0f, 1.0f)); dragStart = null; dragCurrent = null }, onDragCancel = { dragStart = null; dragCurrent = null })
                            InputMode.RAIN -> awaitPointerEventScope { while (true) { val event = awaitPointerEvent(); val change = event.changes.first(); viewModel.setRainPoint(if (change.pressed) change.position else null) } }
                        }
                    }
                }
                .pointerHoverIcon(if (viewModel.inputMode == InputMode.YEET || viewModel.inputMode == InputMode.RAIN) PointerIcon.Crosshair else PointerIcon.Default)
        )

        SandboxHUD(viewModel = viewModel, isLandscape = isLandscape)
        TutorialOverlay(viewModel = viewModel)

        if (viewModel.showRewindDialog) RewindConfirmationDialog(onConfirm = { viewModel.confirmRewind() }, onDismiss = { viewModel.cancelRewind() }, isDonNotAskAgainChecked = !viewModel.isRewindConfirmationRequired, onDonNotAskAgainChanged = { viewModel.isRewindConfirmationRequired = !it })
    }
}

@Composable
fun SandboxHUD(viewModel: SpaceViewModel, isLandscape: Boolean) {
    var showSaveDialog by remember { mutableStateOf(false) }
    var showWebUpsellDialog by remember { mutableStateOf(false) }
    var showSystemTreeModal by remember { mutableStateOf(false) }
    var saveName by remember { mutableStateOf("") }
    Box(modifier = Modifier.fillMaxSize().padding(16.dp)) {
        if (!viewModel.isPhotoMode) {
            TelemetryHUD(
                statsProvider = { viewModel.stats },
                displayStringsProvider = { viewModel.displayStrings },
                planetCountProvider = { viewModel.bodies.size },
                showStatsProvider = { viewModel.showStats },
                performanceMeterVisibleProvider = { viewModel.isPerformanceMeterVisible },
                fpsProvider = { viewModel.fps },
                tpsProvider = { viewModel.tps },
                modifier = Modifier.align(Alignment.TopCenter)
            )

            if (viewModel.activeExtinctionMessage != null) {
                Surface(
                    color = Color.Black.copy(alpha = 0.85f),
                    shape = RoundedCornerShape(16.dp),
                    border = BorderStroke(1.5.dp, Color.Magenta),
                    modifier = Modifier.align(Alignment.TopCenter).padding(top = 65.dp, start = 16.dp, end = 16.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(AppIcons.Public, "Extinction", tint = Color.Magenta, modifier = Modifier.size(18.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            stringResource(Res.string.msg_jordy_extinction).replace("%s", viewModel.activeExtinctionMessage!!),
                            color = Color.Magenta,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }

            if (viewModel.isSystemOffScreen() && !viewModel.isPhotoMode) {
                Surface(
                    onClick = { viewModel.recenterOnGravitationalCenter() },
                    color = Color.Black.copy(alpha = 0.85f),
                    shape = RoundedCornerShape(20.dp),
                    border = BorderStroke(1.5.dp, Color.Cyan),
                    modifier = Modifier
                        .align(Alignment.TopCenter)
                        .padding(top = 65.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(AppIcons.Public, "Recenter", tint = Color.Cyan, modifier = Modifier.size(16.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            stringResource(Res.string.badge_panned_recenter),
                            color = Color.Cyan,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            } else if (viewModel.scale <= 0.03f && !viewModel.isPhotoMode) {
                Surface(
                    color = Color.Black.copy(alpha = 0.85f),
                    shape = RoundedCornerShape(20.dp),
                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.4f)),
                    modifier = Modifier
                        .align(Alignment.TopCenter)
                        .padding(top = 65.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 14.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            stringResource(Res.string.badge_zoomed_info),
                            color = Color.White.copy(alpha = 0.85f),
                            fontSize = 11.sp,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
            Column(modifier = Modifier.align(Alignment.TopStart)) {
                Button(onClick = { viewModel.backToTitle() }, colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.4f))) { Text(stringResource(Res.string.btn_menu), color = Color.White) }
                Spacer(modifier = Modifier.height(8.dp))
                Row(verticalAlignment = Alignment.CenterVertically) {
                    IconButton(onClick = { viewModel.toggleStats() }, colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.1f))) { Icon(AppIcons.BarChart, "Telemetry", tint = Color.Cyan) }
                    Spacer(modifier = Modifier.width(4.dp))
                    IconButton(onClick = { viewModel.togglePause() }, colors = IconButtonDefaults.iconButtonColors(containerColor = if (viewModel.isPaused) Color.Cyan.copy(alpha = 0.3f) else Color.White.copy(alpha = 0.1f))) { Icon(AppIcons.AcUnit, "Freeze", tint = if (viewModel.isPaused) Color.Cyan else Color.White) }
                    if (viewModel.currentScreen is Screen.Sandbox && (viewModel.currentScreen as Screen.Sandbox).mode == GameMode.STABLE_SYSTEM) {
                        Spacer(modifier = Modifier.width(4.dp))
                        IconButton(onClick = { viewModel.toggleHabitableZone() }, colors = IconButtonDefaults.iconButtonColors(containerColor = if (viewModel.isHabitableZoneVisible) Color.Green.copy(alpha = 0.3f) else Color.White.copy(alpha = 0.1f))) { Icon(AppIcons.Eco, "Life Zone", tint = if (viewModel.isHabitableZoneVisible) Color.Green else Color.White) }
                    }
                    Spacer(modifier = Modifier.width(4.dp))
                    IconButton(onClick = { viewModel.togglePhotoMode() }, colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.1f))) { Icon(AppIcons.CameraAlt, "Photo Mode", tint = Color.White) }
                    Spacer(modifier = Modifier.width(4.dp))
                    IconButton(onClick = { showSystemTreeModal = true }, colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.1f))) { Icon(AppIcons.Public, "System Tree", tint = Color.White.copy(alpha = 0.9f)) }
                    if (!isSaveSupported) {
                        Spacer(modifier = Modifier.width(4.dp))
                        IconButton(onClick = { toggleFullscreen() }, colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.15f))) { Text("⛶", color = Color.Cyan, fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                    }
                    Spacer(modifier = Modifier.width(12.dp)); VerticalDivider(modifier = Modifier.height(24.dp), color = Color.White.copy(alpha = 0.2f)); Spacer(modifier = Modifier.width(12.dp))
                    if (viewModel.isRewindEnabled) {
                        IconButton(onClick = { viewModel.triggerRewind() }, colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.15f))) { Icon(AppIcons.History, stringResource(Res.string.tm_title_rewind), tint = Color.Yellow) }
                        Spacer(modifier = Modifier.width(4.dp))
                    }
                    Button(onClick = { viewModel.cycleTimeScale() }, colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.15f)), modifier = Modifier.height(32.dp), contentPadding = PaddingValues(horizontal = 8.dp)) { Text("${viewModel.timeScale}x", color = Color.Cyan, fontSize = 10.sp, fontWeight = FontWeight.ExtraBold) }
                }
                if (viewModel.isCustomPlacing) PlacementHUD(viewModel)
            }
            Column(modifier = Modifier.align(Alignment.BottomEnd), horizontalAlignment = Alignment.End) {
                Row(modifier = Modifier.padding(bottom = 8.dp)) {
                    PlanetSizeSelector("S", PlanetSize.SMALL, viewModel.selectedPlanetSize) { viewModel.selectedPlanetSize = it }
                    Spacer(modifier = Modifier.width(4.dp)); PlanetSizeSelector("M", PlanetSize.MEDIUM, viewModel.selectedPlanetSize) { viewModel.selectedPlanetSize = it }
                    Spacer(modifier = Modifier.width(4.dp)); PlanetSizeSelector("L", PlanetSize.LARGE, viewModel.selectedPlanetSize) { viewModel.selectedPlanetSize = it }
                }
                val isGlowing = viewModel.isTutorialActive && viewModel.tutorialStep in 2..3
                val infiniteTransition = rememberInfiniteTransition(label = "buttonGlow")
                val glowAlpha by infiniteTransition.animateFloat(
                    initialValue = 0.3f,
                    targetValue = 1.0f,
                    animationSpec = infiniteRepeatable(
                        animation = tween(600, easing = LinearEasing),
                        repeatMode = RepeatMode.Reverse
                    ),
                    label = "glowAlpha"
                )

                val mC = when(viewModel.inputMode) { InputMode.OFF -> Color.Gray; InputMode.YEET -> Color.Yellow; InputMode.RAIN -> Color(0xFFBB86FC) }
                val modeLabel = when(viewModel.inputMode) {
                    InputMode.OFF -> stringResource(Res.string.mode_move)
                    InputMode.YEET -> stringResource(Res.string.mode_slingshot)
                    InputMode.RAIN -> stringResource(Res.string.mode_rain)
                }
                Surface(
                    shape = RoundedCornerShape(20.dp),
                    border = if (isGlowing) BorderStroke(2.5.dp, Color(0xFFA855F7).copy(alpha = glowAlpha)) else null,
                    color = Color.Transparent
                ) {
                    Button(
                        onClick = { viewModel.cycleInputMode() },
                        colors = ButtonDefaults.buttonColors(containerColor = mC, contentColor = if (viewModel.inputMode == InputMode.YEET) Color.Black else Color.White),
                        modifier = Modifier.width(115.dp)
                    ) {
                        Text(modeLabel, fontSize = 10.sp, fontWeight = FontWeight.Bold, maxLines = 1)
                    }
                }
                Spacer(modifier = Modifier.height(12.dp))
                Button(onClick = { if (isSaveSupported) { saveName = (viewModel.currentScreen as? Screen.Sandbox)?.savedName ?: ""; showSaveDialog = true } else { showWebUpsellDialog = true } }, colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.4f))) { Text(stringResource(Res.string.btn_save), fontSize = 10.sp, color = Color.White) }
            }

            if (viewModel.isSurfaceViewActive) {
                val followedBody = viewModel.selectedBodyId?.let { id ->
                    viewModel.stars.find { it.id == id } ?: viewModel.bodies.find { it.id == id }
                }
                Surface(
                    color = Color.Black.copy(alpha = 0.8f),
                    shape = RoundedCornerShape(16.dp),
                    modifier = Modifier.align(Alignment.TopCenter).padding(top = 80.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Column {
                            Text(
                                stringResource(Res.string.label_surface_view).replace("%s", followedBody?.let { localizeBodyName(it.name) } ?: ""),
                                color = Color.Cyan,
                                fontSize = 12.sp,
                                fontWeight = FontWeight.Bold
                            )
                            if (followedBody != null) {
                                val periodS = if (followedBody.rotationSpeed != 0f) (360f / followedBody.rotationSpeed.absoluteValue).roundToInt() else 0
                                Text(
                                    "Spin Period: ${periodS}s",
                                    color = Color.White.copy(alpha = 0.7f),
                                    fontSize = 10.sp
                                )
                            }
                        }
                        Button(
                            onClick = { viewModel.exitSurfaceView() },
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFF5252)),
                            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Text(stringResource(Res.string.btn_exit_surface_view), color = Color.White, fontSize = 10.sp, fontWeight = FontWeight.Bold)
                        }
                    }
                }

                if (viewModel.surfacePitchAngle < -50f) {
                    val alpha = ((-50f - viewModel.surfacePitchAngle) / 40f).coerceIn(0f, 1f)
                    Surface(
                        color = Color.Black.copy(alpha = 0.85f * alpha),
                        shape = RoundedCornerShape(20.dp),
                        border = BorderStroke(1.dp, Color.Cyan.copy(alpha = 0.6f * alpha)),
                        modifier = Modifier.align(Alignment.Center).padding(horizontal = 32.dp)
                    ) {
                        Text(
                            text = "Why look at your feet when you can look at the chaotic cosmos?",
                            color = Color.Cyan.copy(alpha = alpha),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center,
                            modifier = Modifier.padding(20.dp)
                        )
                    }
                }
            }
            if (viewModel.selectedBodyId != null && !viewModel.isSurfaceViewActive) {
                val id = viewModel.selectedBodyId!!; val target = viewModel.stars.find { it.id == id } ?: viewModel.bodies.find { it.id == id }
                if (target != null) SelectionHUD(target = target, nebulae = viewModel.nebulae, isFollowing = viewModel.isFollowing, isMinimized = viewModel.isHudMinimized, isLandscape = isLandscape, scannerVersionProvider = { viewModel.scannerVersion }, onToggleFollow = { viewModel.toggleFollow() }, onEnterSurfaceView = { viewModel.enterSurfaceView() }, onToggleMinimize = { viewModel.isHudMinimized = !viewModel.isHudMinimized }, onDismiss = { viewModel.deselectBody() }, modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = if (isLandscape) 16.dp else 80.dp))
            }
            if (showSystemTreeModal) {
                Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.7f)), contentAlignment = Alignment.Center) {
                    SystemLineageFlowchartModal(
                        viewModel = viewModel,
                        selectedId = viewModel.selectedBodyId,
                        onFastTravel = { body ->
                            viewModel.selectedBodyId = body.id
                            viewModel.isFollowing = true
                            showSystemTreeModal = false
                        },
                        onClose = { showSystemTreeModal = false }
                    )
                }
            }
        } else IconButton(onClick = { viewModel.togglePhotoMode() }, modifier = Modifier.align(Alignment.TopEnd).padding(16.dp), colors = IconButtonDefaults.iconButtonColors(containerColor = Color.White.copy(alpha = 0.05f))) { Text("X", color = Color.White.copy(alpha = 0.3f), fontWeight = FontWeight.Bold) }
    }
    if (showSaveDialog) AlertDialog(onDismissRequest = { showSaveDialog = false }, confirmButton = { Button(onClick = { viewModel.saveGame(saveName); showSaveDialog = false }) { Text(stringResource(Res.string.btn_save_confirm)) } }, title = { Text(stringResource(Res.string.title_save_universe)) }, text = { TextField(value = saveName, onValueChange = { saveName = it }) } )
    if (showWebUpsellDialog) {
        AlertDialog(
            onDismissRequest = { showWebUpsellDialog = false },
            containerColor = Color(0xFF1A1A1A),
            titleContentColor = Color.Cyan,
            textContentColor = Color.White,
            title = { Text("Mobile Apps Coming Soon!", fontWeight = FontWeight.ExtraBold) },
            text = {
                Text("Universe saving and offline play will be available in the upcoming CrashBit Mobile App on iOS & Android! Stay tuned!", fontSize = 14.sp)
            },
            confirmButton = {
                Button(
                    onClick = { showWebUpsellDialog = false },
                    colors = ButtonDefaults.buttonColors(containerColor = Color.Cyan, contentColor = Color.Black)
                ) {
                    Text("OK", fontWeight = FontWeight.Bold)
                }
            }
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun TelemetryHUD(
    statsProvider: () -> TelemetryStats,
    displayStringsProvider: () -> TelemetryDisplayStrings,
    planetCountProvider: () -> Int,
    showStatsProvider: () -> Boolean,
    performanceMeterVisibleProvider: () -> Boolean,
    fpsProvider: () -> Int,
    tpsProvider: () -> Float,
    modifier: Modifier = Modifier
) {
    if (showStatsProvider()) {
        val stats = statsProvider()
        val displays = displayStringsProvider()
        Surface(color = Color.Black.copy(alpha = 0.6f), shape = RoundedCornerShape(24.dp), modifier = modifier.padding(top = 8.dp)) {
            FlowRow(modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp), verticalArrangement = Arrangement.Center, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                StatItem(stringResource(Res.string.stat_planets), planetCountProvider().toString(), width = 65.dp)
                StatItem(stringResource(Res.string.stat_stars), stats.starCount.toString(), width = 60.dp)
                StatItem(stringResource(Res.string.stat_bh), stats.blackHoleCount.toString(), width = 45.dp)
                StatItem(stringResource(Res.string.stat_speed), displays.speed, width = 85.dp)
                StatItem(stringResource(Res.string.stat_heaviest), displays.mass, width = 85.dp)
                StatItem(stringResource(Res.string.stat_system), displays.system, width = 95.dp)
                if (performanceMeterVisibleProvider()) {
                    StatItem(stringResource(Res.string.stat_fps), fpsProvider().toString(), width = 50.dp)
                    StatItem(stringResource(Res.string.stat_tps), tpsProvider().toInt().toString(), width = 50.dp)
                }
            }
        }
    }
}

@Composable
fun PlacementHUD(viewModel: SpaceViewModel) {
    Surface(color = Color.Cyan.copy(alpha = 0.2f), shape = RoundedCornerShape(8.dp), modifier = Modifier.padding(top = 12.dp)) {
        Column(modifier = Modifier.padding(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.label_placement_mode), color = Color.Cyan, fontSize = 12.sp, fontWeight = FontWeight.Bold); Spacer(modifier = Modifier.width(12.dp))
                Button(onClick = { viewModel.finishPlacement() }, colors = ButtonDefaults.buttonColors(containerColor = Color.Green.copy(alpha = 0.4f)), modifier = Modifier.height(24.dp), contentPadding = PaddingValues(horizontal = 8.dp)) { Text(stringResource(Res.string.btn_start_placement), fontSize = 10.sp, fontWeight = FontWeight.Bold) }
            }
            Spacer(modifier = Modifier.height(8.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                PlacementSelector(stringResource(Res.string.type_star), PlacementType.STAR, viewModel.selectedPlacementType) { viewModel.selectedPlacementType = it }
                PlacementSelector(stringResource(Res.string.type_nebula), PlacementType.NEBULA, viewModel.selectedPlacementType) { viewModel.selectedPlacementType = it }
                if (viewModel.isCrazyEddie && isSaveSupported) {
                    PlacementSelector(stringResource(Res.string.type_wormhole), PlacementType.WORMHOLE, viewModel.selectedPlacementType) { viewModel.selectedPlacementType = it }
                }
            }
            if (viewModel.selectedPlacementType == PlacementType.STAR) Text("${stringResource(Res.string.stat_stars)}: ${viewModel.stars.size}/10", color = Color.White, fontSize = 10.sp)
        }
    }
}

@Composable
fun RewindConfirmationDialog(onConfirm: () -> Unit, onDismiss: () -> Unit, isDonNotAskAgainChecked: Boolean, onDonNotAskAgainChanged: (Boolean) -> Unit) {
    AlertDialog(onDismissRequest = onDismiss, containerColor = Color(0xFF1A1A1A), titleContentColor = Color.Yellow, textContentColor = Color.White, title = { Text(stringResource(Res.string.title_rewind_dialog), fontWeight = FontWeight.ExtraBold) }, text = { Column { Text(stringResource(Res.string.label_rewind_confirm)); Spacer(modifier = Modifier.height(24.dp)); Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.clickable { onDonNotAskAgainChanged(!isDonNotAskAgainChecked) }) { Checkbox(checked = isDonNotAskAgainChecked, onCheckedChange = onDonNotAskAgainChanged, colors = CheckboxDefaults.colors(checkedColor = Color.Yellow, checkmarkColor = Color.Black)); Text(stringResource(Res.string.label_dont_ask_again), fontSize = 14.sp) } } }, confirmButton = { Button(onClick = onConfirm, colors = ButtonDefaults.buttonColors(containerColor = Color.Yellow, contentColor = Color.Black)) { Text(stringResource(Res.string.btn_confirm_rewind), fontWeight = FontWeight.Bold) } }, dismissButton = { TextButton(onClick = onDismiss) { Text(stringResource(Res.string.btn_cancel_rewind), color = Color.White.copy(alpha = 0.6f)) } } )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun SelectionHUD(target: SpaceBody, nebulae: List<Nebula>, isFollowing: Boolean, isMinimized: Boolean, isLandscape: Boolean, scannerVersionProvider: () -> Int, onToggleFollow: () -> Unit, onEnterSurfaceView: () -> Unit, onToggleMinimize: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier) {
    scannerVersionProvider(); var selectedTab by remember { mutableIntStateOf(0) }; val scrollState = rememberScrollState()
    Surface(color = Color.Black.copy(alpha = 0.85f), shape = RoundedCornerShape(24.dp), modifier = modifier.animateContentSize().fillMaxWidth(if (isMinimized) 0.6f else 0.95f).widthIn(max = 600.dp).heightIn(max = if (isLandscape) 320.dp else 600.dp)) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth()) {
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically) { Text(text = localizeBodyName(target.name), color = Color.White, fontSize = 16.sp, fontWeight = FontWeight.ExtraBold, maxLines = 1, overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis, modifier = Modifier.weight(1f, fill = false)); Spacer(modifier = Modifier.width(4.dp)); Text(stringResource(Res.string.version_edition), color = Color.White.copy(alpha = 0.3f), fontSize = 8.sp) }
                    Text(when { target.isBlackHole -> stringResource(Res.string.type_singularity); target.isStar -> stringResource(Res.string.type_stellar_object); else -> stringResource(Res.string.type_planetary_body) }, color = Color.Cyan.copy(alpha = 0.7f), fontSize = 9.sp, fontWeight = FontWeight.Bold)
                }
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                    IconButton(onClick = onToggleMinimize, modifier = Modifier.size(32.dp)) { Icon(imageVector = if (isMinimized) AppIcons.ExpandMore else AppIcons.ExpandLess, contentDescription = "Expand/Collapse", tint = Color.White.copy(alpha = 0.5f)) }
                    if (isMinimized) Button(onClick = onToggleFollow, colors = ButtonDefaults.buttonColors(containerColor = if (isFollowing) Color.Cyan.copy(alpha = 0.6f) else Color.White.copy(alpha = 0.4f)), modifier = Modifier.height(30.dp), contentPadding = PaddingValues(horizontal = 8.dp)) { Text(if (isFollowing) stringResource(Res.string.label_camera_locked) else stringResource(Res.string.label_camera_follow), fontSize = 9.sp) }
                    IconButton(onClick = onDismiss, modifier = Modifier.size(32.dp)) { Text("X", color = Color.White.copy(alpha = 0.5f), fontWeight = FontWeight.Bold) }
                }
            }
            if (!isMinimized) {
                Spacer(modifier = Modifier.height(if (isLandscape) 4.dp else 12.dp))
                if (nebulae.any { (target.position - it.position).getDistance() < it.radius }) Surface(color = Color.Cyan.copy(alpha = 0.1f), shape = RoundedCornerShape(8.dp), modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)) { Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) { Icon(AppIcons.Eco, "Nebula", tint = Color.Cyan, modifier = Modifier.size(16.dp)); Spacer(modifier = Modifier.width(8.dp)); Text(stringResource(Res.string.label_accreting_nebula), color = Color.Cyan, fontSize = 10.sp, fontWeight = FontWeight.Bold) } }
                TabRow(selectedTabIndex = selectedTab, containerColor = Color.Transparent, contentColor = Color.Cyan, divider = {}) {
                    Tab(selected = selectedTab == 0, onClick = { selectedTab = 0 }) { Text(stringResource(Res.string.tab_scanner), fontSize = 10.sp, fontWeight = FontWeight.Bold, modifier = Modifier.padding(vertical = 8.dp)) }
                    Tab(selected = selectedTab == 1, onClick = { selectedTab = 1 }) { Text(stringResource(Res.string.tab_chemicals), fontSize = 10.sp, fontWeight = FontWeight.Bold, modifier = Modifier.padding(vertical = 8.dp)) }
                }
                Spacer(modifier = Modifier.height(if (isLandscape) 8.dp else 16.dp))
                Column(modifier = Modifier.weight(1f, fill = false).verticalScroll(scrollState)) {
                    if (selectedTab == 0) FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) { val rM = if (isLandscape) Modifier.width(200.dp) else Modifier.fillMaxWidth(); DataRow(stringResource(Res.string.data_mass), formatMass(target.mass), rM); DataRow(stringResource(Res.string.data_radius), "${(target.radius * 10).roundToInt() / 10.0} km", rM); DataRow(stringResource(Res.string.data_temp), "${target.temperature.roundToInt()} K", rM); val stability = if (target.maxObservedDist > 0) (1f - (target.maxObservedDist - (if (target.minObservedDist == Float.MAX_VALUE) 0f else target.minObservedDist)) / target.maxObservedDist).coerceIn(0f, 1f) else 0f; DataRow(stringResource(Res.string.data_stability), "${(stability * 1000).roundToInt() / 10.0}%", rM); if (target.jordyPopulation > 0L) DataRow(stringResource(Res.string.label_jordy_population), "${formatJordyCount(target.jordyPopulation)} Jordys", rM) }
                    else FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) { val iM = if (isLandscape) Modifier.width(250.dp) else Modifier.fillMaxWidth(); target.composition.forEach { (e, r) -> CompositionBar(localizeElement(e), r, Color.Cyan, iM) }; if (target.lifeProgress > 0) { CompositionBar(stringResource(Res.string.data_habitability), target.lifeProgress, Color.Green, iM); if (target.hasAliens || target.alienProgress > 0) CompositionBar(stringResource(Res.string.data_technosignatures), target.alienProgress, Color.Magenta, iM) } }
                }
                Spacer(modifier = Modifier.height(if (isLandscape) 12.dp else 24.dp))
                if (isFollowing) {
                    Button(
                        onClick = onEnterSurfaceView,
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF03DAC5)),
                        modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp)
                    ) {
                        Text(stringResource(Res.string.btn_surface_view), color = Color.Black, fontWeight = FontWeight.Bold)
                    }
                }
                Button(onClick = onToggleFollow, colors = ButtonDefaults.buttonColors(containerColor = if (isFollowing) Color.Cyan.copy(alpha = 0.6f) else Color.White.copy(alpha = 0.4f)), modifier = Modifier.fillMaxWidth()) { Text(if (isFollowing) stringResource(Res.string.btn_release_lock) else stringResource(Res.string.btn_lock_camera)) }
            }
        }
    }
}

@Composable
fun CompositionBar(label: String, ratio: Float, color: Color = Color.Cyan, modifier: Modifier = Modifier) {
    Column(modifier = modifier) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) { Text(label, color = Color.White.copy(alpha = 0.7f), fontSize = 10.sp); Text("${(ratio * 1000).roundToInt() / 10.0}%", color = Color.White, fontSize = 10.sp) }
        Spacer(modifier = Modifier.height(4.dp)); LinearProgressIndicator(progress = { ratio }, modifier = Modifier.fillMaxWidth().height(4.dp), color = color, trackColor = Color.White.copy(alpha = 0.1f), strokeCap = StrokeCap.Round)
    }
}

@Composable
fun DataRow(label: String, value: String, modifier: Modifier = Modifier) {
    Row(modifier = modifier, horizontalArrangement = Arrangement.SpaceBetween) { Text(label, color = Color.White.copy(alpha = 0.5f), fontSize = 12.sp); Text(value, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold) }
}

@Composable
fun StatItem(label: String, value: String, width: androidx.compose.ui.unit.Dp = 70.dp) {
    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.width(width)) {
        Text(text = label, color = Color.Cyan.copy(alpha = 0.7f), fontSize = 8.sp, fontWeight = FontWeight.Bold)
        Text(text = value, color = Color.White, fontSize = 11.sp, fontWeight = FontWeight.ExtraBold, fontFamily = androidx.compose.ui.text.font.FontFamily.Monospace)
    }
}

@Composable
fun PlanetSizeSelector(label: String, size: PlanetSize, current: PlanetSize, onClick: (PlanetSize) -> Unit) {
    val selected = size == current
    OutlinedButton(onClick = { onClick(size) }, modifier = Modifier.size(32.dp), contentPadding = PaddingValues(0.dp), shape = CircleShape, colors = ButtonDefaults.outlinedButtonColors(containerColor = if (selected) Color.White.copy(alpha = 0.2f) else Color.Transparent, contentColor = Color.White)) { Text(label, fontSize = 10.sp) }
}

@Composable
fun PlacementSelector(label: String, type: PlacementType, current: PlacementType, onClick: (PlacementType) -> Unit) {
    val selected = type == current
    OutlinedButton(onClick = { onClick(type) }, modifier = Modifier.height(32.dp), contentPadding = PaddingValues(horizontal = 8.dp), shape = RoundedCornerShape(8.dp), colors = ButtonDefaults.outlinedButtonColors(containerColor = if (selected) Color.Cyan.copy(alpha = 0.2f) else Color.Transparent, contentColor = if (selected) Color.Cyan else Color.White)) { Text(label, fontSize = 10.sp) }
}

@Composable
fun SimulationLayer(
    bodiesProvider: () -> List<SpaceBody>,
    starsProvider: () -> List<SpaceBody>,
    nebulaeProvider: () -> List<Nebula>,
    wormholesProvider: () -> List<Wormhole>,
    effectsProvider: () -> List<VisualEffect>,
    scaleProvider: () -> Float,
    panProvider: () -> Offset,
    selectedBodyIdProvider: () -> Long?,
    isFollowingProvider: () -> Boolean,
    isSurfaceViewActiveProvider: () -> Boolean,
    surfacePitchAngleProvider: () -> Float,
    surfaceYawAngleProvider: () -> Float,
    statsProvider: () -> TelemetryStats,
    gameTimeProvider: () -> Long,
    drawVersionProvider: () -> Int,
    trajectoriesProvider: () -> OracleResult?,
    isHabitableZoneVisibleProvider: () -> Boolean,
    isPausedProvider: () -> Boolean,
    inputModeProvider: () -> InputMode,
    dragStartProvider: () -> Offset?,
    dragCurrentProvider: () -> Offset?,
    pendingWormholeIdProvider: () -> Long?,
    editingWormholeIdProvider: () -> Long?,
    screenCenterProvider: () -> Offset,
    screenSizeProvider: () -> IntSize,
    interpolationAlphaProvider: () -> Float,
    performanceMeterVisibleProvider: () -> Boolean,
    onFpsUpdated: (Int) -> Unit,
    modifier: Modifier = Modifier
) {
    val backgroundStars = remember { List(200) { Offset((Random.nextFloat() * 3000f), (Random.nextFloat() * 3000f)) } }
    var lastFrameTime by remember { mutableLongStateOf(0L) }
    var frameCount by remember { mutableIntStateOf(0) }

    Canvas(modifier = modifier) {
        drawVersionProvider()
        
        if (performanceMeterVisibleProvider()) {
            val now = currentTimeMillis()
            if (lastFrameTime == 0L) lastFrameTime = now
            else {
                frameCount++
                if (now - lastFrameTime >= 1000L) { onFpsUpdated(frameCount); frameCount = 0; lastFrameTime = now }
            }
        }

        val scale = scaleProvider(); val gameTime = gameTimeProvider(); val bodies = bodiesProvider(); val stars = starsProvider(); val stats = statsProvider(); val screenCenter = screenCenterProvider(); val selectedId = selectedBodyIdProvider(); val isFollowing = isFollowingProvider()
        val followedBody = if (isFollowing && selectedId != null) (stars.find { it.id == selectedId } ?: bodies.find { it.id == selectedId }) else null
        val followedPos = followedBody?.renderPosition; val effectivePan = if (followedPos != null) -followedPos else panProvider()
        val interpolationAlpha = interpolationAlphaProvider()

        val isSurfaceView = isSurfaceViewActiveProvider() && followedBody != null
        val pitchAngle = surfacePitchAngleProvider()
        val yawAngle = surfaceYawAngleProvider()

        with(SpaceRenderer) {
            val obsAngle = if (followedBody != null) lerpRotation(followedBody.previousRotation, followedBody.rotation, interpolationAlpha) else 0f
            val obsPos = followedBody?.let { lerp(it.previousPosition, it.renderPosition, interpolationAlpha) } ?: Offset.Zero

            if (isSurfaceView && followedBody != null) {
                val skyBodies = bodies.filter { it.id != followedBody.id }
                val skyStars = stars.filter { it.id != followedBody.id }
                
                val rad = (obsAngle * PI / 180.0)
                val surfaceOffset = Offset(cos(rad).toFloat(), sin(rad).toFloat()) * followedBody.radius
                val obsWorldPos = obsPos + surfaceOffset
                
                val totalRotation = obsAngle + yawAngle
                
                // Landscape-optimized orbital sky scale projection
                val starDist = skyStars.map { (it.position - obsPos).getDistance() }.minOrNull() ?: 1500f
                val skyScale = (size.height * 0.45f / starDist.coerceAtLeast(200f)).coerceIn(0.05f, 0.6f)
                
                val horizonY = size.height / 2f + (pitchAngle / 90f) * (size.height * 0.5f)
                val horizonCenter = Offset(screenCenter.x, horizonY)

                drawBackgroundStars(backgroundStars, -obsWorldPos, 1.0f)
                withTransform({
                    translate(horizonCenter.x, horizonCenter.y)
                    rotate(-totalRotation - 90f, Offset.Zero)
                    scale(skyScale, skyScale, Offset.Zero)
                    translate(-obsWorldPos.x, -obsWorldPos.y)
                }) {
                    drawVisualEffects(effectsProvider(), skyStars, skyScale, gameTime, interpolationAlpha)
                    drawNebulae(nebulaeProvider(), gameTime)
                    drawWormholes(wormholesProvider(), gameTime, skyScale)
                    drawStars(skyStars, skyScale, gameTime, interpolationAlpha)
                    drawPlanets(skyBodies, skyStars, stats, -obsWorldPos, skyScale, horizonCenter, gameTime, interpolationAlpha, null, null)
                }
                drawSurfaceHorizon(followedBody, pitchAngle)
                drawSurfaceIndicators(skyStars, obsWorldPos, totalRotation, skyScale, horizonY, screenCenter, gameTime, interpolationAlpha)
            } else {
                drawBackgroundStars(backgroundStars, effectivePan, scale)
                withTransform({ translate(screenCenter.x, screenCenter.y); scale(scale, scale, Offset.Zero); translate(effectivePan.x, effectivePan.y) }) {
                    drawVisualEffects(effectsProvider(), stars, scale, gameTime, interpolationAlpha); drawNebulae(nebulaeProvider(), gameTime); drawWormholes(wormholesProvider(), gameTime, scale); drawStars(stars, scale, gameTime, interpolationAlpha)
                    if (isHabitableZoneVisibleProvider()) drawHabitableZones(stars, scale, interpolationAlpha)
                    if (isPausedProvider()) { val trajectories = trajectoriesProvider(); drawTrajectories(trajectories, stats, scale); drawPlanets(bodies, stars, stats, effectivePan, scale, screenCenter, gameTime, interpolationAlpha, selectedId, followedPos); drawCollisionMarkers(trajectories, scale) }
                    else drawPlanets(bodies, stars, stats, effectivePan, scale, screenCenter, gameTime, interpolationAlpha, selectedId, followedPos)
                    val dS = dragStartProvider(); val dC = dragCurrentProvider()
                    if (dS != null && dC != null && inputModeProvider() == InputMode.YEET) { drawLine(color = Color.White.copy(alpha = 0.5f), start = dS, end = dC, strokeWidth = 2f / scale); val launchV = dS - dC; drawLine(color = Color.White.copy(alpha = 0.8f), start = dS, end = dS + launchV * 1.5f, strokeWidth = 3f / scale, pathEffect = PathEffect.dashPathEffect(floatArrayOf(15f, 15f), 0f)); drawCircle(color = Color.White, radius = 4f / scale, center = dS) }
                    val pendingId = pendingWormholeIdProvider()
                    if (pendingId != null) { val firstH = wormholesProvider().find { it.id == pendingId }; if (firstH != null) drawCircle(color = firstH.color.copy(alpha = 0.3f), radius = firstH.radius * 1.2f, center = firstH.position, style = Stroke(width = 2f / scale, pathEffect = PathEffect.dashPathEffect(floatArrayOf(20f, 20f), 0f))) }
                    val editingId = editingWormholeIdProvider()
                    if (editingId != null) { val hole = wormholesProvider().find { it.id == editingId }; if (hole != null) { val angleRad = (hole.exitAngle * PI / 180.0); drawLine(color = hole.color.copy(alpha = 0.6f), start = hole.position, end = hole.position + Offset(cos(angleRad).toFloat() * 10000f, sin(angleRad).toFloat() * 10000f), strokeWidth = 3f / scale, pathEffect = PathEffect.dashPathEffect(floatArrayOf(20f, 20f), 0f)) } }
                }
                drawIndicators(stars, bodies, effectsProvider(), stats, effectivePan, scale, screenCenter, gameTime, interpolationAlpha)
            }
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun SystemLineageFlowchartModal(
    viewModel: SpaceViewModel,
    selectedId: Long?,
    onFastTravel: (SpaceBody) -> Unit,
    onClose: () -> Unit
) {
    Surface(
        color = Color.Black.copy(alpha = 0.94f),
        shape = RoundedCornerShape(24.dp),
        border = BorderStroke(1.5.dp, Color.Cyan.copy(alpha = 0.8f)),
        modifier = Modifier.fillMaxWidth(0.92f).fillMaxHeight(0.88f)
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth()
            ) {
                Column {
                    Text("SYSTEM FAST TRAVEL & FLOWCHART", color = Color.Cyan, fontSize = 15.sp, fontWeight = FontWeight.ExtraBold)
                    Text("Tap Any Celestial Body Below to Instantly Fast Travel & Lock Camera", color = Color.White.copy(alpha = 0.6f), fontSize = 10.sp)
                }
                IconButton(onClick = onClose, modifier = Modifier.size(36.dp)) {
                    Text("X", color = Color.White, fontSize = 16.sp, fontWeight = FontWeight.Bold)
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            Column(modifier = Modifier.weight(1f).verticalScroll(rememberScrollState())) {
                Text("STARS & SINGULARITIES", color = Color.Yellow, fontSize = 11.sp, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(8.dp))
                FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    viewModel.stars.sortedByDescending { it.mass }.forEach { star ->
                        val isSelected = star.id == selectedId
                        Surface(
                            color = if (isSelected) Color.Cyan.copy(alpha = 0.3f) else Color.White.copy(alpha = 0.08f),
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, if (isSelected) Color.Cyan else Color.White.copy(alpha = 0.2f)),
                            modifier = Modifier.clickable { onFastTravel(star) }.width(180.dp)
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                if (isSelected) Text("★ SCANNED OBJECT", color = Color.Cyan, fontSize = 8.sp, fontWeight = FontWeight.ExtraBold)
                                Text(localizeBodyName(star.name), color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                                Text(if (star.isBlackHole) "Singularity" else "Star", color = Color.Yellow, fontSize = 10.sp)
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(20.dp))
                Text("PLANETARY SYSTEM TREE", color = Color.Green, fontSize = 11.sp, fontWeight = FontWeight.Bold)
                Spacer(modifier = Modifier.height(8.dp))

                FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    viewModel.bodies.sortedByDescending { it.mass }.forEach { planet ->
                        val isSelected = planet.id == selectedId
                        Surface(
                            color = if (isSelected) Color.Cyan.copy(alpha = 0.35f) else Color.White.copy(alpha = 0.08f),
                            shape = RoundedCornerShape(12.dp),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, if (isSelected) Color.Cyan else Color.White.copy(alpha = 0.2f)),
                            modifier = Modifier.clickable { onFastTravel(planet) }.width(220.dp)
                        ) {
                            Column(modifier = Modifier.padding(12.dp)) {
                                if (isSelected) Text("★ SCANNED WORLD", color = Color.Cyan, fontSize = 9.sp, fontWeight = FontWeight.ExtraBold)
                                Text(localizeBodyName(planet.name), color = Color.White, fontSize = 13.sp, fontWeight = FontWeight.ExtraBold)
                                Text("Mass: ${formatMass(planet.mass)}", color = Color.White.copy(alpha = 0.7f), fontSize = 10.sp)
                                if (planet.jordyPopulation > 0L) {
                                    Text("Jordys: ${formatJordyCount(planet.jordyPopulation)}", color = Color.Magenta, fontSize = 10.sp, fontWeight = FontWeight.Bold)
                                }
                                if (planet.parentIds.isNotEmpty()) {
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text("Ancestry: Merged from Parents #${planet.parentIds.joinToString { "${it % 10000}" }}", color = Color.Yellow, fontSize = 9.sp)
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun formatMass(mass: Float): String {
    return when {
        mass >= 1e12f -> "${(mass / 1e11f).roundToInt() / 10.0}T T"
        mass >= 1e9f -> "${(mass / 1e8f).roundToInt() / 10.0}B T"
        mass >= 1e6f -> "${(mass / 1e5f).roundToInt() / 10.0}M T"
        else -> "${mass.toInt()}T"
    }
}

@Composable
private fun localizeElement(key: String): String = when (key) {
    "Hydrogen" -> stringResource(Res.string.chem_hydrogen); "Helium" -> stringResource(Res.string.chem_helium); "Metals" -> stringResource(Res.string.chem_metals); "Iron" -> stringResource(Res.string.chem_iron); "Silicates" -> stringResource(Res.string.chem_silicates); "Water" -> stringResource(Res.string.chem_water); "Carbon" -> stringResource(Res.string.chem_carbon); "Dark Matter" -> stringResource(Res.string.chem_dark_matter); else -> key
}

@Composable
private fun localizeBodyName(name: String): String = when {
    name.startsWith("Planet-") -> name.replace("Planet", stringResource(Res.string.name_planet)); name.startsWith("Singularity-") -> name.replace("Singularity", stringResource(Res.string.name_singularity)); name.startsWith("Proto-Star-") -> name.replace("Proto-Star", stringResource(Res.string.name_proto_star)); name.startsWith("Nebula-") -> name.replace("Nebula", stringResource(Res.string.name_nebula)); name.endsWith("-Debris") -> name.replace("Debris", stringResource(Res.string.name_debris)); name.endsWith("-Fragment") -> name.replace("Fragment", stringResource(Res.string.name_fragment)); else -> name
}

private fun Offset.getDistance() = sqrt((x * x + y * y).toDouble()).toFloat()

@Composable
fun TutorialOverlay(viewModel: SpaceViewModel) {
    if (!viewModel.isTutorialActive) return

    val step = viewModel.tutorialStep
    val step1Desc = if (isSaveSupported) stringResource(Res.string.tutorial_step1_desc) else stringResource(Res.string.tutorial_step1_desc_web)
    val (stepTitle, stepDesc) = when (step) {
        1 -> stringResource(Res.string.tutorial_step1_title) to step1Desc
        2 -> stringResource(Res.string.tutorial_step2_title) to stringResource(Res.string.tutorial_step2_desc)
        3 -> stringResource(Res.string.tutorial_step3_title) to stringResource(Res.string.tutorial_step3_desc)
        4 -> stringResource(Res.string.tutorial_step4_title) to stringResource(Res.string.tutorial_step4_desc)
        else -> stringResource(Res.string.tutorial_step5_title) to stringResource(Res.string.tutorial_step5_desc)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .padding(bottom = 85.dp, start = 12.dp, end = 140.dp)
    ) {
        Surface(
            color = Color(0xFF0F0B1E).copy(alpha = 0.94f),
            shape = RoundedCornerShape(16.dp),
            border = BorderStroke(1.5.dp, Color(0xFFA855F7)),
            modifier = Modifier
                .align(Alignment.BottomStart)
                .fillMaxWidth()
                .widthIn(max = 420.dp)
        ) {
            Row(
                modifier = Modifier
                    .padding(horizontal = 14.dp, vertical = 10.dp)
                    .fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column(modifier = Modifier.weight(1f).padding(end = 8.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(
                            stringResource(Res.string.tutorial_title),
                            color = Color(0xFFA855F7),
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            letterSpacing = 1.sp
                        )
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(
                            stepTitle,
                            color = Color.Cyan,
                            fontSize = 10.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Spacer(modifier = Modifier.height(2.dp))
                    Text(
                        stepDesc,
                        color = Color.White,
                        fontSize = 10.sp,
                        lineHeight = 13.sp
                    )
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    TextButton(
                        onClick = { viewModel.completeTutorial() },
                        contentPadding = PaddingValues(horizontal = 4.dp, vertical = 2.dp)
                    ) {
                        Text(
                            stringResource(Res.string.tutorial_btn_skip),
                            color = Color.White.copy(alpha = 0.5f),
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Spacer(modifier = Modifier.width(2.dp))
                    Button(
                        onClick = { viewModel.advanceTutorialStep() },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFA855F7)),
                        contentPadding = PaddingValues(horizontal = 10.dp, vertical = 4.dp),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text(
                            if (step < 5) stringResource(Res.string.tutorial_btn_next) else stringResource(Res.string.tutorial_btn_finish),
                            color = Color.White,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
    }
}
